import { IonIcon, IonItem, IonLabel } from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type UserDevice } from "@/lib/backend/api";
import { chevronRightIcon } from "@/lib/icons/chevron-right.icon";
import { Routes } from "@/lib/router";

function useFormatRelativeTime() {
  const { t } = useTranslation();

  return (date: string): string => {
    const now = Date.now();
    const then = new Date(date).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) {
      return t("settings.notifications.relative-time.just-now");
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return t("settings.notifications.relative-time.minutes", { count: minutes });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return t("settings.notifications.relative-time.hours", { count: hours });
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return t("settings.notifications.relative-time.days", { count: days });
    }
    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return t("settings.notifications.relative-time.weeks", { count: weeks });
    }
    const months = Math.floor(days / 30);
    if (months < 12) {
      return t("settings.notifications.relative-time.months", { count: months });
    }
    const years = Math.floor(days / 365);
    return t("settings.notifications.relative-time.years", { count: years });
  };
}

interface OtherDevicesCardProps {
  devices: UserDevice[];
  currentDeviceId: string;
}

export function OtherDevicesCard({ devices, currentDeviceId }: OtherDevicesCardProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const formatRelativeTime = useFormatRelativeTime();

  const otherDevices = devices.filter((device) => device.deviceIdentifier !== currentDeviceId);

  if (otherDevices.length === 0) {
    return null;
  }

  const deviceCount = otherDevices.length;

  return (
    <div className="mx-4 overflow-hidden rounded-lg bg-blue-50 dark:bg-blue-900/20">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/30"
      >
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
            <svg
              className="size-5 text-blue-600 dark:text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {t("settings.notifications.other-devices.title")}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {t("settings.notifications.other-devices.count", { count: deviceCount })}
            </p>
          </div>
        </div>
        <IonIcon
          icon={chevronRightIcon}
          className={`size-5 text-blue-600 transition-transform dark:text-blue-400 ${
            isExpanded ? "rotate-90" : ""
          }`}
        />
      </button>

      {/* Expanded device list */}
      {isExpanded && (
        <div className="border-t border-blue-200 dark:border-blue-800">
          <ul className="divide-y divide-blue-200 dark:divide-blue-800">
            {otherDevices.map((device) => (
              <li key={device.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <PlatformIcon platform={device.platform} />
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100">{device.name}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      {formatRelativeTime(device.createdAt)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Manage all devices button */}
          <IonItem
            routerLink={Routes.NotificationsDevices()}
            detail
            detailIcon={chevronRightIcon}
            className="--background-activated:blue-100 dark:--background-activated:blue-900/30 --background-hover:blue-100 dark:--background-hover:blue-900/30 --background:transparent"
            lines="none"
          >
            <IonLabel className="text-blue-900 dark:text-blue-100">
              {t("settings.notifications.other-devices.view-all")}
            </IonLabel>
          </IonItem>
        </div>
      )}
    </div>
  );
}

function PlatformIcon({ platform }: { platform?: string | null }) {
  if (platform === "ios") {
    return (
      <svg
        className="size-5 text-blue-600 dark:text-blue-400"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
    );
  }

  if (platform === "android") {
    return (
      <svg
        className="size-5 text-blue-600 dark:text-blue-400"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.46 11.46 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
      </svg>
    );
  }

  // Default/unknown platform
  return (
    <svg
      className="size-5 text-blue-600 dark:text-blue-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}
